/**
 * Native FE-compute evaluator (Phase 1).
 *
 * Evaluates the bounded "native" formula grammar the backend emits in the NativeFormulaBundle —
 * the SAME expression string the server evaluates with (e.g. `field('Qty') * field('Price')`),
 * so a value computed here matches the server's authoritative recompute on submit. This is the
 * clean replacement for the legacy eval()-based front-trigger path: a real tokenizer + parser,
 * no eval(), and an explicit, version-gated function/operator set.
 *
 * Supported (v1): numeric/string/bool/null literals; field('Display Name'); iif(cond,a,b);
 * hasValue(x); parentheses; unary - and !; * / %; + -; comparisons; && ||; ternary ?:.
 * Added in v2: rowNumber() -- the current table row's 1-based position (per-row/column context).
 * Added in v3: today() / now() -- the current local date / instant as an ISO-8601 string. The
 * clock is INJECTED by the caller (opts.today / opts.now) so this evaluator stays pure and
 * deterministic; the value is computed in the user's timezone (advertised to the server via the
 * `tz` request header) so the server's authoritative submit-time recompute matches.
 * v4 is a CONTRACT version, no new expression tokens: it advertises that this client consumes
 * NativeBundleField.controls (client-side visible/readOnly/required with AutoFallback-parity
 * coercion), fullyClientOwned trigger suppression, and the v4 fullyClientComputable semantics
 * (nativeCompute.js). All v4 behavior is keyed off the bundle's ECHOED version — a pre-v4
 * backend echoes <= 3 and this client falls back to v3 behavior automatically (D-7,
 * docs/native-bundle-v4-control-formulas-proposal.md).
 * The backend's NativeBundleBuilder gates expressions against exactly this set, so anything
 * outside it never reaches the client (it stays on the server sync path).
 */

/* v5: the spreadsheet easy tier (spreadsheet-formula-surface Phase 3) — IF AND OR NOT CONCAT
 * LEN LEFT RIGHT MID TRIM UPPER LOWER ROUND, with EXCEL semantics matching the server's
 * ExcelFunctions.cs byte-for-byte (display coercions, ROUND half-away-from-zero incl.
 * negative digits, throw-on-invalid degrading to server sync). Parity is pinned by the
 * SHARED formula-eval-vectors.json (twin copy in FETIAS.Business.Tests/Automation). TEXT
 * and YEAR/MONTH/DAY stay server-tier until the format-parity suite exists.
 * v6: TRUNC, repeating ExcelFunctions.Trunc's arithmetic (same vectors). */
const NATIVE_ENGINE_VERSION = 6;

/* ------------------------------------------------------------------ tokenizer */

const PUNCT = ["&&", "||", "==", "!=", "<=", ">=", "(", ")", ",", "+", "-", "*", "/", "%", "<", ">", "!", "?", ":"];

function tokenize(src) {
  const tokens = [];
  let i = 0;
  const n = src.length;

  while (i < n) {
    const c = src[i];

    // whitespace
    if (c === " " || c === "\t" || c === "\r" || c === "\n") { i++; continue; }

    // string literal with \ escapes (the native converter emits double quotes; single also accepted)
    if (c === "'" || c === '"') {
      const quote = c;
      let j = i + 1;
      let val = "";
      while (j < n && src[j] !== quote) {
        if (src[j] === "\\" && j + 1 < n) { val += src[j + 1]; j += 2; }
        else { val += src[j]; j++; }
      }
      if (j >= n) throw new Error("Unterminated string literal");
      tokens.push({ t: "str", v: val });
      i = j + 1;
      continue;
    }

    // number (int / float)
    if (c >= "0" && c <= "9") {
      let j = i;
      while (j < n && ((src[j] >= "0" && src[j] <= "9") || src[j] === ".")) j++;
      tokens.push({ t: "num", v: parseFloat(src.slice(i, j)) });
      i = j;
      continue;
    }

    // identifier / keyword
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < n && /[A-Za-z0-9_]/.test(src[j])) j++;
      tokens.push({ t: "ident", v: src.slice(i, j) });
      i = j;
      continue;
    }

    // multi-char then single-char punctuation
    const two = src.substr(i, 2);
    if (PUNCT.indexOf(two) > -1) { tokens.push({ t: "op", v: two }); i += 2; continue; }
    if (PUNCT.indexOf(c) > -1) { tokens.push({ t: "op", v: c }); i++; continue; }

    throw new Error(`Unexpected character '${c}' at ${i}`);
  }

  tokens.push({ t: "eof" });
  return tokens;
}

/* -------------------------------------------------------------------- parser
   Recursive descent, lowest precedence first:
   ternary -> or -> and -> equality -> comparison -> additive -> multiplicative -> unary -> primary
   AST nodes: {k:'num'|'str'|'bool'|'null'|'bin'|'un'|'tern'|'call', ...}                       */

function parse(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const eat = (v) => {
    const tk = tokens[pos];
    if ((tk.t === "op" && tk.v === v) || (tk.t === "ident" && tk.v === v)) { pos++; return tk; }
    throw new Error(`Expected '${v}' but got '${tk.v ?? tk.t}'`);
  };
  const isOp = (v) => { const tk = tokens[pos]; return tk.t === "op" && tk.v === v; };

  function parseTernary() {
    const cond = parseOr();
    if (isOp("?")) {
      next();
      const a = parseTernary();
      eat(":");
      const b = parseTernary();
      return { k: "tern", cond, a, b };
    }
    return cond;
  }

  function binLevel(subParser, ops) {
    let left = subParser();
    while (peek().t === "op" && ops.indexOf(peek().v) > -1) {
      const op = next().v;
      const right = subParser();
      left = { k: "bin", op, left, right };
    }
    return left;
  }

  const parseOr = () => binLevel(parseAnd, ["||"]);
  const parseAnd = () => binLevel(parseEquality, ["&&"]);
  const parseEquality = () => binLevel(parseComparison, ["==", "!="]);
  const parseComparison = () => binLevel(parseAdditive, ["<", ">", "<=", ">="]);
  const parseAdditive = () => binLevel(parseMultiplicative, ["+", "-"]);
  const parseMultiplicative = () => binLevel(parseUnary, ["*", "/", "%"]);

  function parseUnary() {
    if (isOp("-") || isOp("!")) {
      const op = next().v;
      return { k: "un", op, arg: parseUnary() };
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const tk = peek();

    if (isOp("(")) { next(); const e = parseTernary(); eat(")"); return e; }

    if (tk.t === "num") { next(); return { k: "num", v: tk.v }; }
    if (tk.t === "str") { next(); return { k: "str", v: tk.v }; }

    if (tk.t === "ident") {
      const name = next().v;
      if (name === "true" || name === "True") return { k: "bool", v: true };
      if (name === "false" || name === "False") return { k: "bool", v: false };
      if (name === "null" || name === "None") return { k: "null" };

      // function call
      eat("(");
      const args = [];
      if (!isOp(")")) {
        args.push(parseTernary());
        while (isOp(",")) { next(); args.push(parseTernary()); }
      }
      eat(")");
      return { k: "call", name, args };
    }

    throw new Error(`Unexpected token '${tk.v ?? tk.t}'`);
  }

  const ast = parseTernary();
  if (peek().t !== "eof") throw new Error(`Unexpected trailing token '${peek().v}'`);
  return ast;
}

/* ----------------------------------------------------------------- evaluator */

function toNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function isBlank(v) {
  return v === null || v === undefined || v === "";
}

function hasValue(v) {
  return !isBlank(v);
}

/* numeric op for the per-row aggregate helpers (sumColCol/sumColConst); division-by-zero -> 0,
   matching the scalar `/` operator and the server's ApplyOp. */
function applyArith(a, op, b) {
  switch (op) {
    case "+": return a + b;
    case "-": return a - b;
    case "*": return a * b;
    case "/": return b === 0 ? 0 : a / b;
  }
  return 0;
}

function evalNode(node, ctx) {
  switch (node.k) {
    case "num": return node.v;
    case "str": return node.v;
    case "bool": return node.v;
    case "null": return null;

    case "un":
      if (node.op === "-") return -toNum(evalNode(node.arg, ctx));
      return !truthy(evalNode(node.arg, ctx));

    case "tern":
      return truthy(evalNode(node.cond, ctx)) ? evalNode(node.a, ctx) : evalNode(node.b, ctx);

    case "bin": {
      const op = node.op;
      // short-circuit logicals
      if (op === "&&") return truthy(evalNode(node.left, ctx)) && truthy(evalNode(node.right, ctx));
      if (op === "||") return truthy(evalNode(node.left, ctx)) || truthy(evalNode(node.right, ctx));

      const l = evalNode(node.left, ctx);
      const r = evalNode(node.right, ctx);
      switch (op) {
        case "+":
          // string concat when either side is a non-numeric string
          if (typeof l === "string" || typeof r === "string") {
            const ls = l === null || l === undefined ? "" : l;
            const rs = r === null || r === undefined ? "" : r;
            if (typeof ls === "string" || typeof rs === "string") return `${ls}${rs}`;
          }
          return toNum(l) + toNum(r);
        case "-": return toNum(l) - toNum(r);
        case "*": return toNum(l) * toNum(r);
        case "/": { const d = toNum(r); return d === 0 ? 0 : toNum(l) / d; }
        case "%": { const d = toNum(r); return d === 0 ? 0 : toNum(l) % d; }
        case "==": return looseEq(l, r);
        case "!=": return !looseEq(l, r);
        case "<": return toNum(l) < toNum(r);
        case ">": return toNum(l) > toNum(r);
        case "<=": return toNum(l) <= toNum(r);
        case ">=": return toNum(l) >= toNum(r);
      }
      throw new Error(`Unknown operator ${op}`);
    }

    case "call": {
      const name = node.name;
      if (name === "field") {
        const dn = evalNode(node.args[0], ctx);
        return readField(ctx, dn);
      }
      if (name === "getVariable") {
        /* getVariable("Entry"|"Form", "Display Name"). On the server the two scopes are NOT the
           same thing:
             "Form"  -> the live form value (formRetrieval.Default, this form's fields only).
             "Entry" -> the SAVED record, read straight off the entry and independent of the form
                        (locus.ExtensionReporting, module-wide field set). It does not move while
                        the form is open even if the same field is edited on screen, and it covers
                        fields the form does not show at all.
           The client is never sent that saved snapshot, so it can only offer form data. Which is
           acceptable depends on what the result feeds -- see opts.strictEntryScope on evaluate(). */
        const scope = evalNode(node.args[0], ctx);
        if (scope !== "Entry" && scope !== "Form") throw new Error(`Unsupported variable scope ${scope}`);
        const dn = evalNode(node.args[1], ctx);
        if (scope === "Entry" && ctx.strictEntryScope) {
          throw new Error(`Entry scope ('${dn}') is server-resolved`);
        }
        return readField(ctx, dn);
      }
      if (name === "colField") {
        // colField("Column") -> the sibling column's value in the CURRENT table row. Only valid when
        // evaluating a column (per-row) formula; ctx.col is the row accessor.
        if (!ctx.col) throw new Error("colField used outside a table row context");
        const cn = evalNode(node.args[0], ctx);
        return ctx.col(cn);
      }
      if (name === "rowNumber") {
        // rowNumber() -> the current table row's 1-based position (rowAutoNumber columns). Only valid
        // per-row; ctx.rowNumber is the 1-based index the caller supplies. Mirrors the server's
        // formulaRowNumber (IndexOf(row) + 1). Throwing outside a row context degrades to sync,
        // leaving the value untouched -- matching the server's field-level rowNumber() no-op.
        if (ctx.rowNumber == null) throw new Error("rowNumber used outside a table row context");
        return ctx.rowNumber;
      }
      if (name === "today") {
        // today() -> the current local date as an ISO-8601 string at local midnight, e.g.
        // "2026-06-23T00:00:00.000+08:00" (the same shape a date field stores). The caller injects
        // it (opts.today) so the evaluator is deterministic; the server computes the SAME string in
        // the client's timezone, so a value shown here equals the submit-time recompute. A missing
        // clock throws -> degrades to sync (mirrors rowNumber()/colField() outside their context).
        if (ctx.today == null) throw new Error("today() has no injected clock");
        return ctx.today;
      }
      if (name === "now") {
        // now() -> the current local instant as an ISO-8601 string (e.g.
        // "2026-06-23T14:30:45.123+08:00"). Same injection/parity contract as today(); the sub-second
        // drift between this eval and the server submit is expected and the submit value is authoritative.
        if (ctx.now == null) throw new Error("now() has no injected clock");
        return ctx.now;
      }
      if (name === "iif") {
        // lazy branches: only evaluate the taken side
        return truthy(evalNode(node.args[0], ctx))
          ? evalNode(node.args[1], ctx)
          : evalNode(node.args[2], ctx);
      }
      if (name === "hasValue") {
        return hasValue(evalNode(node.args[0], ctx));
      }
      // ---- table aggregates (a whole-column total, computed on-device over the live rows) ----
      // The first arg is the scope ("Entry"/"Form"); on the client both mean the live form, so it is
      // ignored. ctx.rows(tableDisplayName) yields the table's rows as entities resolving field() by
      // display name. Mirrors the server's sumColumn/sumColCol/sumColConst native helpers exactly.
      if (name === "sumColumn") {
        // sumColumn(scope, table, column)
        const rows = ctx.rows ? ctx.rows(evalNode(node.args[1], ctx)) : [];
        const column = evalNode(node.args[2], ctx);
        let total = 0;
        for (const r of rows) total += toNum(r.field(column));
        return total;
      }
      if (name === "sumColCol") {
        // sumColCol(scope, table, colA, op, colB) -> sum of a per-row two-column expression
        const rows = ctx.rows ? ctx.rows(evalNode(node.args[1], ctx)) : [];
        const colA = evalNode(node.args[2], ctx);
        const op = evalNode(node.args[3], ctx);
        const colB = evalNode(node.args[4], ctx);
        let total = 0;
        for (const r of rows) total += applyArith(toNum(r.field(colA)), op, toNum(r.field(colB)));
        return total;
      }
      if (name === "sumColConst") {
        // sumColConst(scope, table, colA, op, const) -> sum of a per-row column-vs-constant expression
        const rows = ctx.rows ? ctx.rows(evalNode(node.args[1], ctx)) : [];
        const colA = evalNode(node.args[2], ctx);
        const op = evalNode(node.args[3], ctx);
        const constB = toNum(evalNode(node.args[4], ctx));
        let total = 0;
        for (const r of rows) total += applyArith(toNum(r.field(colA)), op, constB);
        return total;
      }
      // ---- v5: the spreadsheet easy tier — Excel semantics (ExcelFunctions.cs parity).
      // Invalid input THROWS, so the caller's fail-open path degrades to server sync where
      // the server's answer/error is authoritative — never a silently divergent result.
      if (name === "IF") {
        // eager on the server (C# delegate args), lazy here — pure expressions, same result
        return excelTruthy(evalNode(node.args[0], ctx))
          ? evalNode(node.args[1], ctx)
          : evalNode(node.args[2], ctx);
      }
      if (name === "AND") {
        for (const a of node.args) if (!excelTruthy(evalNode(a, ctx))) return false;
        return true;
      }
      if (name === "OR") {
        for (const a of node.args) if (excelTruthy(evalNode(a, ctx))) return true;
        return false;
      }
      if (name === "NOT") {
        return !excelTruthy(evalNode(node.args[0], ctx));
      }
      if (name === "CONCAT") {
        let out = "";
        for (const a of node.args) out += excelStr(evalNode(a, ctx));
        return out;
      }
      if (name === "LEN") {
        return excelStr(evalNode(node.args[0], ctx)).length;
      }
      if (name === "LEFT") {
        const text = excelStr(evalNode(node.args[0], ctx));
        const count = node.args.length > 1 ? excelCount(evalNode(node.args[1], ctx), "LEFT") : 1;
        return text.slice(0, Math.min(count, text.length));
      }
      if (name === "RIGHT") {
        const text = excelStr(evalNode(node.args[0], ctx));
        const count = node.args.length > 1 ? excelCount(evalNode(node.args[1], ctx), "RIGHT") : 1;
        return text.slice(text.length - Math.min(count, text.length));
      }
      if (name === "MID") {
        const text = excelStr(evalNode(node.args[0], ctx));
        const from = excelToInt(evalNode(node.args[1], ctx), "MID");
        const count = excelToInt(evalNode(node.args[2], ctx), "MID");
        if (from < 1 || count < 0) throw new Error("MID: start must be >= 1 and length >= 0");
        if (from > text.length) return "";
        return text.substr(from - 1, Math.min(count, text.length - (from - 1)));
      }
      if (name === "TRIM") {
        return excelStr(evalNode(node.args[0], ctx)).trim().replace(/ {2,}/g, " ");
      }
      if (name === "UPPER") {
        return excelStr(evalNode(node.args[0], ctx)).toUpperCase();
      }
      if (name === "LOWER") {
        return excelStr(evalNode(node.args[0], ctx)).toLowerCase();
      }
      if (name === "ROUND") {
        const x = excelToNumber(evalNode(node.args[0], ctx), "ROUND");
        const digits = excelToInt(evalNode(node.args[1], ctx), "ROUND");
        if (digits >= 0) return excelRoundAway(x, Math.min(digits, 15));
        const factor = Math.pow(10, -digits);
        return excelRoundAway(x / factor, 0) * factor;
      }
      // ---- v6: TRUNC (the converter's spelling of Python's int(x))
      if (name === "TRUNC") {
        const x = excelToNumber(evalNode(node.args[0], ctx), "TRUNC");
        const digits = node.args.length > 1 ? excelToInt(evalNode(node.args[1], ctx), "TRUNC") : 0;
        return excelTrunc(x, digits);
      }
      throw new Error(`Unsupported function ${name}`);
    }
  }
  throw new Error(`Unknown node ${node.k}`);
}

/* Read a live-form field by display name (field() and Form-scoped getVariable()).
   formData carries only the fields this activity form shows, so a read of anything else has no
   value on the client -- entity.field() returns undefined, which is indistinguishable from a
   field that is present and empty. Evaluating against that phantom empty would silently produce a
   different answer than the server's. Throw instead, so the caller's fail-open path (D-3) hands
   the control/value back to the server, whose answer is authoritative.
   ctx.has is absent for entities that expose no schema -> unchanged behavior. */
function readField(ctx, displayName) {
  if (ctx.has && !ctx.has(displayName)) {
    throw new Error(`Field '${displayName}' is not on this form`);
  }
  return ctx.field(displayName);
}

function truthy(v) {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  return true;
}

/* ---- v5 helpers: Excel semantics, ExcelFunctions.cs parity ---------------------------- */

/* ExcelFunctions.Truthy: scalar rules match truthy(); a collection counts its items
   (plural fields arrive as arrays — an empty list is falsy, matching the C# ICollection rule). */
function excelTruthy(v) {
  if (Array.isArray(v)) return v.length > 0;
  return truthy(v);
}

/* ExcelFunctions.DoubleStr ("0.##############"): up to 14 decimals, trailing zeros trimmed. */
function excelNumStr(n) {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(14).replace(/0+$/, "").replace(/\.$/, "");
}

/* ExcelFunctions.ExcelStr: blank → "", TRUE/FALSE, numbers without a trailing .0. Date
   values reach the client as ISO strings and pass through unchanged (the server's DateTime
   branch formats to the same ISO shape). Anything else throws — degrading to sync, where
   the server's error is authoritative. */
function excelStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (typeof v === "number") return excelNumStr(v);
  throw new Error("A value of this kind cannot be used as text.");
}

/* ExcelFunctions.ToDouble: null / non-numeric text throws (the server's
   "value is not a number" contract); numeric strings parse InvariantCulture-style. */
function excelToNumber(v, fn) {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (isFinite(n)) return n;
  }
  throw new Error(`${fn}: the value is not a number`);
}

/* ExcelFunctions.ToInt (Convert.ToInt32): banker's rounding on midpoints. */
function excelToInt(v, fn) {
  const n = excelToNumber(v, fn);
  const floor = Math.floor(n);
  const diff = n - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

/* ExcelFunctions.CountArg: LEFT/RIGHT counts must be >= 0. */
function excelCount(v, fn) {
  const n = excelToInt(v, fn);
  if (n < 0) throw new Error(`${fn}: count must be >= 0`);
  return n;
}

/* Math.Round(x, digits, MidpointRounding.AwayFromZero). */
function excelRoundAway(x, digits) {
  const factor = Math.pow(10, digits);
  const scaled = x * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return rounded / factor;
}

/* ExcelFunctions.Trunc, the same arithmetic: toward zero; with digits, a scaled value
   within 1e-15 of a whole number is that number (0.29 * 100 is 28.999999999999996). */
function excelTrunc(x, digits) {
  if (digits === 0) return Math.trunc(x);
  const n = Math.max(-15, Math.min(digits, 15));
  const factor = Math.pow(10, Math.abs(n));
  const scaled = n > 0 ? x * factor : x / factor;
  const whole = Math.floor(scaled + 0.5);
  const kept = Math.abs(scaled - whole) <= Math.abs(scaled) * 1e-15 ? whole : Math.trunc(scaled);
  return n > 0 ? kept / factor : kept * factor;
}

function looseEq(l, r) {
  /* Form controls represent an unselected value as either null/undefined, while native formulas
     compare it with the empty-string literal emitted by the designer. Treat those three blank
     representations as the same value so `field != ""` does not re-show a hidden control
     (SS05950). */
  if (isBlank(l) && isBlank(r)) return true;
  if (typeof l === "number" || typeof r === "number") return toNum(l) === toNum(r);
  return l === r;
}

/* --------------------------------------------------------------- public API */

const _cache = {};

/** Compile an expression to an AST (memoized). Throws on a grammar error. */
function compile(expression) {
  if (_cache[expression]) return _cache[expression];
  const ast = parse(tokenize(expression));
  _cache[expression] = ast;
  return ast;
}

/**
 * Evaluate a native expression against an entity exposing field(displayName).
 * Returns { ok, value } — ok:false (with the value left untouched by the caller) on any
 * tokenize/parse/eval error, so a malformed/unsupported expression degrades safely.
 *
 * For a per-row column formula, pass the row entity as `row`: colField("Col") then resolves
 * against the row, while field()/getVariable() still resolve against the outer entity.
 *
 * `opts.rows(tableDisplayName)` supplies a table's rows as entities (each exposing field(displayName))
 * for the table aggregates sumColumn/sumColCol/sumColConst; omit it when no aggregate is used.
 * `opts.rowNumber` is the current row's 1-based position, supplied for rowNumber() (rowAutoNumber).
 * `opts.today` / `opts.now` are the current local date / instant as ISO-8601 strings, supplied for
 * today() / now(); inject them from the caller's clock so this evaluator stays deterministic.
 *
 * `opts.strictEntryScope` refuses getVariable("Entry", …) instead of answering it from form data.
 * Set it wherever the on-device result must agree with what the server ALREADY computed:
 *   - control formulas (visible/readOnly/required) — set. The server resolved these at form load
 *     against the saved entry and shipped the answer on the element; a local re-evaluation that
 *     reads form data instead overrides a correct answer with a different variable's value, and it
 *     sticks until the next sync. That is SS05941 (a "Status" removed from the form read as empty,
 *     so `Entry's Status != 'Paid'` came out true and re-showed a hidden section).
 *   - value formulas — leave unset. The Information designer routes every computed-field reference
 *     through the Entry root (FTSInformationDesignModal.formulaVariables), so refusing Entry would
 *     take the whole computed-field lane off-device. These are a preview of the SUBMIT-time
 *     recompute, where the server's Entry is the about-to-be-saved record — i.e. form data — and
 *     the server overwrites the value authoritatively on submit either way.
 * The lasting fix for both is the bundle shipping the saved values of its Entry-scoped
 * dependencies, which would let this resolve Entry properly instead of choosing a lane.
 */
function evaluate(expression, entity, row, opts) {
  try {
    const ast = compile(expression);
    const value = evalNode(ast, {
      field: (dn) => entity.field(dn),
      /* schema-backed membership test: lets field()/getVariable() distinguish "on the form and
         empty" from "not on the form at all". Only available when the entity carries a schema
         (FETIASEntity); a bare { field } stub keeps the permissive behavior. */
      has: entity && Array.isArray(entity.schema)
        ? (dn) => dn === "id" || entity.schema.some(x => x && x.displayName === dn)
        : null,
      col: row ? (cn) => row.field(cn) : null,
      rows: opts && opts.rows ? opts.rows : null,
      strictEntryScope: !!(opts && opts.strictEntryScope),
      rowNumber: opts && opts.rowNumber != null ? opts.rowNumber : null,
      today: opts && opts.today != null ? opts.today : null,
      now: opts && opts.now != null ? opts.now : null
    });
    return { ok: true, value };
  } catch (err) {
    if (typeof console !== "undefined") console.warn("nativeFormula:", expression, err && err.message);
    return { ok: false, value: undefined };
  }
}

export default { NATIVE_ENGINE_VERSION, compile, evaluate };
export { NATIVE_ENGINE_VERSION, compile, evaluate };
